import { useState } from 'react';
import { RefreshCw, AlertTriangle, LifeBuoy } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useSupportRequests, useUpdateSupportRequest } from '../hooks/useAdminOps';
import { type SupportRequestItem, type SupportStatus } from '../api/adminOps';
import {
  QueueTable,
  StatusChip,
  DetailDrawer,
  type ChipTone,
  type QueueColumn,
} from '../components/ui';

type StatusFilter = SupportStatus | 'all';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'in_review', label: 'In review' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'all', label: 'All' },
];

const STATUS_TONES: Record<string, { label: string; tone: ChipTone }> = {
  new: { label: 'New', tone: 'info' },
  in_review: { label: 'In review', tone: 'warn' },
  resolved: { label: 'Resolved', tone: 'ok' },
};

function statusChip(status: string) {
  const config = STATUS_TONES[status] ?? { label: status, tone: 'neutral' as ChipTone };
  return <StatusChip label={config.label} tone={config.tone} />;
}

function sourceLabel(source: SupportRequestItem['source']) {
  switch (source) {
    case 'app_authenticated':
      return 'App';
    case 'website_support':
      return 'Website';
    case 'formspree_import':
      return 'Formspree import';
  }
}

/** First-response SLA: 24h target, warn past 12h elapsed, crit past 18h. */
const FRT_SLA_HOURS = 24;
const FRT_WARN_HOURS = 12;
const FRT_CRIT_HOURS = 18;

function frtChip(row: SupportRequestItem) {
  if (row.source === 'formspree_import') {
    return <StatusChip label="FRT unknown" tone="neutral" />;
  }
  if (row.status !== 'new' || row.first_responded_at) {
    return row.first_responded_at ? (
      <StatusChip label="FRT met" tone="ok" />
    ) : (
      <StatusChip label="FRT —" tone="neutral" />
    );
  }
  const elapsedHours = (Date.now() - new Date(row.created_at).getTime()) / 3_600_000;
  const tone: ChipTone =
    elapsedHours > FRT_CRIT_HOURS ? 'crit' : elapsedHours > FRT_WARN_HOURS ? 'warn' : 'ok';
  if (elapsedHours >= FRT_SLA_HOURS) {
    return <StatusChip label="FRT breached" tone="crit" />;
  }
  const hoursLeft = Math.max(0, Math.floor(FRT_SLA_HOURS - elapsedHours));
  return <StatusChip label={`FRT ${hoursLeft}h left`} tone={tone} />;
}

function DetailField({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <p className="text-xs text-gray-text uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm text-white break-words">{value ?? '—'}</p>
    </div>
  );
}

export default function Support() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('new');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<SupportStatus>('new');
  const [draftExternalRef, setDraftExternalRef] = useState('');

  const queue = useSupportRequests(statusFilter === 'all' ? {} : { status: statusFilter });
  const updateMutation = useUpdateSupportRequest();

  const selected = queue.rows.find(row => row.id === selectedId) ?? null;

  const openRow = (row: SupportRequestItem) => {
    setSelectedId(row.id);
    setDraftStatus((STATUS_TONES[row.status] ? row.status : 'new') as SupportStatus);
    setDraftExternalRef(row.external_ref ?? '');
  };

  const closeDrawer = () => setSelectedId(null);

  const saveChanges = () => {
    if (!selected) return;
    updateMutation.mutate(
      {
        id: selected.id,
        update: {
          status: draftStatus,
          external_ref: draftExternalRef.trim() === '' ? null : draftExternalRef.trim(),
        },
      },
      { onSuccess: closeDrawer }
    );
  };

  const columns: QueueColumn<SupportRequestItem>[] = [
    {
      key: 'created',
      header: 'Created',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">
          {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
        </span>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      render: row => (
        <div>
          <p className="text-white">{row.subject ?? '(no subject)'}</p>
          {row.category && <p className="text-xs text-gray-text mt-0.5">{row.category}</p>}
        </div>
      ),
    },
    {
      key: 'reference',
      header: 'Reference',
      render: row => <span className="text-gray-text">{row.reference ?? '—'}</span>,
    },
    {
      key: 'source',
      header: 'Source',
      render: row => <span className="text-gray-text">{sourceLabel(row.source)}</span>,
    },
    { key: 'status', header: 'Status', render: row => statusChip(row.status) },
    { key: 'frt', header: 'FRT SLA', render: row => frtChip(row) },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <LifeBuoy className="w-7 h-7 text-primary" />
            Support inbox
          </h1>
          <p className="text-gray-text">
            Triage app and website support requests. First-response target is {FRT_SLA_HOURS}h.
          </p>
        </div>
        <button
          onClick={() => queue.refetch()}
          className="btn-secondary flex items-center space-x-2"
          disabled={queue.isFetching}
        >
          <RefreshCw className={`w-4 h-4 ${queue.isFetching ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Status filter chips */}
      <div className="glass-card p-4 flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-text mr-2">Status:</span>
        {STATUS_FILTERS.map(filter => (
          <button
            key={filter.value}
            onClick={() => {
              setStatusFilter(filter.value);
              setSelectedId(null);
            }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              statusFilter === filter.value
                ? 'bg-primary/20 text-primary'
                : 'bg-gray-500/10 text-gray-text hover:text-white'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Queue */}
      {queue.isError ? (
        <div className="glass-card p-6 border border-red-500/30 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-medium mb-1">Failed to load support requests</p>
            <p className="text-sm text-gray-text mb-3">
              {queue.error instanceof Error ? queue.error.message : 'Unknown error'}
            </p>
            <button onClick={() => queue.refetch()} className="btn-secondary">
              Retry
            </button>
          </div>
        </div>
      ) : (
        <QueueTable
          columns={columns}
          rows={queue.rows}
          rowKey={row => row.id}
          isLoading={queue.isPending}
          onRowClick={openRow}
          emptyTitle="No support requests"
          emptyHint={
            statusFilter === 'all'
              ? 'The support inbox is empty.'
              : `No requests with status "${statusFilter}".`
          }
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onLoadMore={() => queue.fetchNextPage()}
        />
      )}

      {/* Detail drawer */}
      <DetailDrawer
        open={selected !== null}
        title={selected?.subject ?? 'Support request'}
        onClose={closeDrawer}
        footer={
          selected && (
            <div className="space-y-3">
              <div>
                <label className="label" htmlFor="support-status">
                  Status
                </label>
                <select
                  id="support-status"
                  className="input"
                  value={draftStatus}
                  onChange={event => setDraftStatus(event.target.value as SupportStatus)}
                >
                  <option value="new">New</option>
                  <option value="in_review">In review</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
              <div>
                <label className="label" htmlFor="support-external-ref">
                  External ref (ticketing system)
                </label>
                <input
                  id="support-external-ref"
                  className="input"
                  placeholder="e.g. ZD-12345"
                  value={draftExternalRef}
                  onChange={event => setDraftExternalRef(event.target.value)}
                />
              </div>
              <button
                onClick={saveChanges}
                disabled={updateMutation.isPending}
                className="btn-primary w-full disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          )
        }
      >
        {selected && (
          <>
            <div className="flex items-center gap-2">
              {statusChip(selected.status)}
              {frtChip(selected)}
            </div>
            <DetailField label="Subject" value={selected.subject} />
            <DetailField label="Category" value={selected.category} />
            <DetailField
              label="Source"
              value={
                selected.source === 'website_support'
                  ? 'Website Support Center'
                  : sourceLabel(selected.source)
              }
            />
            <DetailField label="Reference" value={selected.reference} />
            <DetailField label="External ref" value={selected.external_ref} />
            <DetailField label="Created" value={new Date(selected.created_at).toLocaleString()} />
            <DetailField
              label="First responded"
              value={
                selected.first_responded_at
                  ? new Date(selected.first_responded_at).toLocaleString()
                  : null
              }
            />
            <DetailField
              label="Resolved"
              value={selected.resolved_at ? new Date(selected.resolved_at).toLocaleString() : null}
            />
            <DetailField
              label="Last meaningful activity"
              value={
                selected.last_meaningful_activity_at
                  ? new Date(selected.last_meaningful_activity_at).toLocaleString()
                  : null
              }
            />
            <DetailField label="Requester name" value={selected.requester_name} />
            <DetailField label="Requester email" value={selected.requester_email} />
            <DetailField label="Requester user id" value={selected.user_id} />
            <div className="border-t border-gray-border pt-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-text/70 mb-1">
                Message
              </p>
              <p className="text-sm text-white whitespace-pre-wrap break-words">
                {selected.message}
              </p>
            </div>
          </>
        )}
      </DetailDrawer>
    </div>
  );
}
